import { supabase } from '@/integrations/supabase/client';
import { 
  Review, 
  ReviewTargetType, 
  CreateReviewData, 
  UpdateReviewData,
  RatingStats,
  ReviewVote, 
  ReviewFlag, 
  FlagType,
  CategoryRatings
} from '@/types/reviews';

// Enhanced review with user info
export interface ReviewWithUser extends Review {
  user: {
    id: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
  helpful_votes_count?: number;
  total_votes_count?: number;
}

function isValidUUID(uuid: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
}

function isValidDisplayId(id: string): boolean {
  // Display IDs are integers (bigint)
  return /^\d+$/.test(id);
}

function isValidTargetId(targetType: ReviewTargetType, targetId: string): boolean {
  if (targetType === 'display') {
    return isValidDisplayId(targetId);
  } else if (targetType === 'sequence') {
    return isValidUUID(targetId);
  }
  return false;
}

export class ReviewService {
  /**
   * Create a new review
   */
  static async createReview(data: CreateReviewData): Promise<Review> {
    try {
      // Get the current user ID from auth
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User must be authenticated to create review');
      }

      // Prepare insert data based on target type
      const insertData: any = {
        target_type: data.target_type,
        user_id: user.id,
        rating: data.rating,
        title: data.title || null,
        content: data.content || null,
        recommended: data.recommended,
        category_ratings: data.category_ratings || null,
      };

      // Handle different target ID types
      if (data.target_type === 'display') {
        insertData.target_id_bigint = parseInt(data.target_id);
        insertData.target_id = data.target_id; // Keep as string for compatibility
      } else {
        insertData.target_id = data.target_id;
      }

      const { data: review, error } = await supabase
        .from('reviews')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return review as Review;
    } catch (error) {
      console.error('Error creating review:', error);
      throw new Error('Failed to create review');
    }
  }

  /**
   * Update an existing review
   */
  static async updateReview(reviewId: string, data: UpdateReviewData): Promise<Review> {
    try {
      const updateData: any = {};

      if (data.rating !== undefined) updateData.rating = data.rating;
      if (data.title !== undefined) updateData.title = data.title || null;
      if (data.content !== undefined) updateData.content = data.content || null;
      if (data.recommended !== undefined) updateData.recommended = data.recommended;
      if (data.category_ratings !== undefined) updateData.category_ratings = data.category_ratings || null;

      const { data: review, error } = await supabase
      .from('reviews')
      .update(updateData)
      .eq('id', reviewId)
      .select()
      .single();

    if (error) throw error;
      return review as Review;
    } catch (error) {
      console.error('Error updating review:', error);
      throw new Error('Failed to update review');
    }
  }

  /**
   * Delete a review
   */
  static async deleteReview(reviewId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting review:', error);
      throw new Error('Failed to delete review');
    }
  }

  /**
   * Get reviews for a specific target with user information
   */
  static async getReviews(
    targetType: ReviewTargetType,
    targetId: string,
    options?: {
      limit?: number;
      offset?: number;
      sortBy?: 'created_at' | 'rating' | 'helpful_votes';
      sortOrder?: 'asc' | 'desc';
      status?: string;
    }
  ): Promise<ReviewWithUser[]> {
    if (!isValidTargetId(targetType, targetId)) {
      console.warn('ReviewService.getReviews: targetId is not a valid target:', targetId);
      return [];
    }
    try {
      let query = supabase
        .from('enhanced_review_summary')
        .select('*')
        .eq('target_type', targetType)
        .eq('status', options?.status || 'published');

      // Handle different target ID types
      if (targetType === 'display') {
        // For displays, use the bigint column (displays.id is bigint)
        query = query.eq('target_id_bigint', targetId);
      } else {
        // For sequences, use the UUID target_id
        query = query.eq('target_id', targetId);
      }

      // Apply sorting
      const sortBy = options?.sortBy || 'created_at';
      const sortOrder = options?.sortOrder || 'desc';
      
      if (sortBy === 'helpful_votes') {
        query = query.order('helpful_votes', { ascending: sortOrder === 'asc' });
      } else {
        query = query.order(sortBy, { ascending: sortOrder === 'asc' });
      }

      // Apply pagination
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      if (options?.offset) {
        query = query.range(options.offset, (options.offset + (options.limit || 10)) - 1);
      }

      const { data: reviews, error } = await query;

      if (error) throw error;

      // Transform the enhanced_review_summary data to our ReviewWithUser format
      const reviewsWithUser = reviews?.map(review => ({
        ...review,
        category_ratings: review.category_ratings as CategoryRatings,
        helpful_votes_count: review.helpful_votes || 0,
        total_votes_count: (review.helpful_votes || 0) + (review.not_helpful_votes || 0),
        user: {
          id: review.user_id || '',
          first_name: review.reviewer_name || 'Anonymous',
          last_name: '', // enhanced_review_summary only has reviewer_name
          avatar_url: review.reviewer_avatar || 'https://vhaizqhkodqyqplpqcss.supabase.co/storage/v1/object/public/avatars/default-avatar.png'
        }
      })) || [];

      return reviewsWithUser as ReviewWithUser[];
    } catch (error) {
      console.error('Error fetching reviews:', error);
      throw new Error('Failed to fetch reviews');
    }
  }

  /**
   * Get rating statistics for a target
   */
  static async getRatingStats(
    targetType: ReviewTargetType,
    targetId: string
  ): Promise<RatingStats> {
    try {
      // For sequences, calculate stats from reviews table
      if (targetType === 'sequence') {
        const { data: reviews, error } = await supabase
          .from('reviews')
          .select('rating')
          .eq('target_type', 'sequence')
          .eq('target_id', targetId)
          .eq('status', 'published');

        if (error) throw error;

        if (!reviews || reviews.length === 0) {
          return {
            average_rating: 0,
            rating_count: 0,
            rating_distribution: { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 }
          };
        }

        // Calculate average rating
        const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
        const averageRating = totalRating / reviews.length;

        // Calculate rating distribution
        const distribution = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
        reviews.forEach(review => {
          distribution[review.rating.toString() as keyof typeof distribution]++;
        });

        return {
          average_rating: Math.round(averageRating * 100) / 100, // Round to 2 decimal places
          rating_count: reviews.length,
          rating_distribution: distribution
        };
      }

      // For displays, query the actual table
      const { data, error } = await supabase
        .from('displays')
        .select('average_rating, rating_count, rating_distribution')
        .eq('id', targetId)
        .single();

      if (error) throw error;

      return {
        average_rating: data.average_rating || 0,
        rating_count: data.rating_count || 0,
        rating_distribution: (data.rating_distribution as any) || { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 }
      };
    } catch (error) {
      console.error('Error fetching rating stats:', error);
      // Return default stats if error
      return {
        average_rating: 0,
        rating_count: 0,
        rating_distribution: { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 }
      };
    }
  }

  /**
   * Get user's review for a specific target
   */
  static async getUserReview(
    targetType: ReviewTargetType,
    targetId: string,
    userId: string
  ): Promise<Review | null> {
    if (!isValidTargetId(targetType, targetId)) {
      console.warn('ReviewService.getUserReview: targetId is not a valid target:', targetId);
      return null;
    }
    try {
      let query = supabase
        .from('reviews')
        .select('*')
        .eq('target_type', targetType)
        .eq('user_id', userId);

      // Handle different target ID types
      if (targetType === 'display') {
        // For displays, use the bigint column (displays.id is bigint)
        query = query.eq('target_id_bigint', targetId);
      } else {
        // For sequences, use the UUID target_id
        query = query.eq('target_id', targetId);
      }

      const { data: review, error } = await query.maybeSingle();

      if (error) throw error;
      return review ? { ...review, category_ratings: review.category_ratings as CategoryRatings } as Review : null;
    } catch (error) {
      console.error('Error fetching user review:', error);
      return null;
    }
  }

  /**
   * Check if user can review a target (not already reviewed)
   */
  static async canUserReview(
    targetType: ReviewTargetType,
    targetId: string,
    userId: string
  ): Promise<boolean> {
    try {
      const existingReview = await this.getUserReview(targetType, targetId, userId);
      return !existingReview;
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      return false;
    }
  }

  /**
   * Vote on a review (helpful/not helpful)
   */
  static async voteOnReview(
    reviewId: string,
    userId: string,
    voteType: 'helpful' | 'not_helpful'
  ): Promise<ReviewVote> {
    try {
      const { data: vote, error } = await supabase
        .from('review_votes')
        .upsert({
          review_id: reviewId,
          user_id: userId,
          vote_type: voteType
        }, {
          onConflict: 'review_id,user_id'
        })
        .select()
        .single();

      if (error) throw error;
      return vote as ReviewVote;
    } catch (error) {
      console.error('Error voting on review:', error);
      throw new Error('Failed to vote on review');
    }
  }

  /**
   * Remove vote from a review
   */
  static async removeVote(reviewId: string, userId: string): Promise<void> {
    try {
    const { error } = await supabase
      .from('review_votes')
      .delete()
      .eq('review_id', reviewId)
      .eq('user_id', userId);

    if (error) throw error;
    } catch (error) {
      console.error('Error removing vote:', error);
      throw new Error('Failed to remove vote');
    }
  }

  /**
   * Flag a review for moderation
   */
  static async flagReview(
    reviewId: string,
    userId: string,
    flagType: FlagType,
    reason?: string
  ): Promise<ReviewFlag> {
    try {
      const { data: flag, error } = await supabase
      .from('review_flags')
      .insert({
        review_id: reviewId,
        user_id: userId,
        flag_type: flagType,
        reason: reason || null
      })
      .select()
      .single();

    if (error) throw error;
      return flag as ReviewFlag;
    } catch (error) {
      console.error('Error flagging review:', error);
      throw new Error('Failed to flag review');
        }
  }

  /**
   * Get user's vote on a specific review
   */
  static async getUserVote(reviewId: string, userId: string): Promise<ReviewVote | null> {
    try {
      const { data: vote, error } = await supabase
        .from('review_votes')
      .select('*')
        .eq('review_id', reviewId)
      .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      return vote as ReviewVote;
    } catch (error) {
      console.error('Error fetching user vote:', error);
      return null;
    }
  }

  /**
   * Check if user has purchased a sequence (for verified purchase reviews)
   */
  static async hasUserPurchased(sequenceId: string, userId: string): Promise<boolean> {
    try {
      // Check if user has a successful purchase record for this sequence
      const { data: purchase, error } = await supabase
        .from('purchases')
      .select('id')
        .eq('sequence_id', sequenceId)
        .eq('user_id', userId)
        .eq('status', 'completed')
        .maybeSingle();

      if (error) {
        console.error('Error checking purchase status:', error);
        return false;
      }

      return !!purchase;
    } catch (error) {
      console.error('Error checking purchase status:', error);
      return false;
    }
  }

  /**
   * Validate review data before submission
   */
  static validateReviewData(data: CreateReviewData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Rating validation
    if (!data.rating || data.rating < 1 || data.rating > 5) {
      errors.push('Rating must be between 1 and 5 stars');
  }

    // Title validation
    if (data.title && data.title.length > 200) {
      errors.push('Title must be 200 characters or less');
  }

    // Content validation
    if (data.content && data.content.length > 2000) {
      errors.push('Review content must be 2000 characters or less');
  }

    // Target validation
    if (!data.target_type || !['display', 'sequence'].includes(data.target_type)) {
      errors.push('Invalid target type');
    }

    if (!data.target_id) {
      errors.push('Target ID is required');
    }

    // Category ratings validation
    if (data.category_ratings) {
      for (const [category, rating] of Object.entries(data.category_ratings)) {
        if (typeof rating !== 'number' || rating < 1 || rating > 5) {
          errors.push(`Category rating for ${category} must be between 1 and 5`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default ReviewService; 